"use client"

import { useEffect, useId, useRef, useState, type FormEvent } from "react"
import Link from "next/link"
import { AlertCircle, ArrowRight, Check, CheckCircle, Info, Loader2, Lock } from "lucide-react"
import { useFeatureFlagEnabled } from "posthog-js/react"

import { currentQuotaMonth, type AnonQuotaReason } from "@/lib/anon-quota-flag"
import {
  analytics,
  runWhenPageVisible,
  stashEmailCaptureAttribution,
  surfaceFromPathname,
  trackEvent,
} from "@/lib/analytics"
import {
  ANONYMOUS_WEB_ANALYSIS_LIMITS,
  EMAIL_TIER_ANALYSIS_LIMITS,
} from "@/lib/auth/feature-flags"
import { detectEmailTypo, type EmailSuggestion } from "@/lib/email-typo-detection"
import { ensureAnalysisFunnelId } from "@/lib/analysis-funnel-id"
import { Button } from "@/components/ui/button"
import { SocialLoginButtons } from "@/components/auth/SocialLoginButtons"
import { APPLE_GLYPH_PATH, AppDownloadTextLink, AppStoreLinkButton } from "@/components/ui/app-download-cta"
import { AppStoreQrCode } from "@/components/ui/app-store-qr"
import { appInstallAvailableNow, useAppInstallAvailable } from "@/hooks/use-app-install-available"
import { coarsePointerNow, useCoarsePointer } from "@/hooks/use-coarse-pointer"
import type { AnalysisMode as SharedAnalysisMode } from "@/lib/types/diagnosis"
import type { RefinementTrigger } from "@/lib/analysis/refinement-policy"
import type { PhotoLimitReason } from "@/lib/analysis/photo-caps"

type AnalysisMode = SharedAnalysisMode

function resultLabels(mode: AnalysisMode) {
  if (mode === 'diagnose') return { singular: 'diagnosis', plural: 'diagnoses' }
  if (mode === 'weed') return { singular: 'weed ID', plural: 'weed IDs' }
  return { singular: 'grass ID', plural: 'grass IDs' }
}

/**
 * Names the QUOTA BUCKET, not the result. Weed deliberately shares the
 * diagnosis allowance (see featureForMode in lib/auth/usage-tracking.ts), so
 * "N more weed IDs" over-promises: those N are shared with diagnoses, and a
 * user can hit this wall on a weed scan having only ever run a diagnosis.
 *
 * Use resultLabels for the thing the user gets back ("this photo's weed ID")
 * and allowanceLabels for anything counting against a limit.
 */
function allowanceLabels(mode: AnalysisMode) {
  if (mode === 'identify') return { singular: 'grass ID', plural: 'grass IDs' }
  return { singular: 'diagnosis', plural: 'diagnoses' }
}

/**
 * Distinct app-store placement per gate frame so each one's CTR stays
 * separable. Derived, never passed in, so every call site is covered.
 */
function photoGatePlacement(
  mode: AnalysisMode,
  intent: 'photo_cap' | 'refinement',
): string {
  const slug = mode === 'diagnose' ? 'diagnose' : mode === 'weed' ? 'weed_id' : 'grass_id'
  const suffix = intent === 'photo_cap' ? 'photo_cap_gate' : 'refinement_gate'
  return `${slug}_${suffix}`
}

/** Trailing clarifier for modes whose allowance is shared with another mode. */
function allowanceNote(mode: AnalysisMode): string {
  return mode === 'weed' ? ' Weed IDs share your diagnosis allowance.' : ''
}

/**
 * Names what the visitor gets back, in their own climate. The ZIP is already in
 * hand — they typed it one step before this wall — so the ask can promise a
 * specific result instead of a generic one. A missing or half-typed ZIP simply
 * drops the clause rather than rendering "tuned to ZIP undefined"; every caller
 * passes raw form state, which is empty until the field validates.
 */
function payoffLabel(mode: AnalysisMode, zipCode?: string): string {
  const tuned = zipCode && /^\d{5}$/.test(zipCode) ? ` tuned to ZIP ${zipCode}` : ''
  if (mode === 'diagnose') return `the likely cause, how to fix it, and a 12-month plan${tuned}`
  if (mode === 'weed') return `the weed name, how to treat it, and timing${tuned}`
  return `your species, a confidence score, and a 12-month plan${tuned}`
}

// Shared limit walls for the homepage + /diagnose analyzers. Both pages
// previously carried verbatim copies of these two blocks; the copy here is the
// single source of truth.
//
// Copy framing (godaddy-checkout "honest quota framing" + linkedin-notifications
// "ask at the moment of invested effort"): the user has already picked a photo
// and often typed a ZIP, so the ask names the thing being unlocked — THIS
// photo's result — before the generic benefits. The photo + ZIP are persisted
// to localStorage before this wall renders (pendingAnalysis_limit), so
// "your photo stays right here" is a real promise, not marketing.
//
// THREE LANES, ON PURPOSE (2026-08-07). The wall serves the conversion triad:
// account (OAuth), email, and app install. It led with the app from 2026-08-02
// on the argument that an install converts ~70x better per exposure than a web
// upgrade prompt, with OAuth demoted under a divider and no email lane at all.
//
// What restores the lanes is the wall's SIZE, not a verdict on that argument.
// Over 28 days (2026-07-10 to 2026-08-06), of 571 people who reached it:
// 38.9% started OAuth and 28.9% completed signup, which is 55% of ALL site
// signups in the window. 7.0% gave an email (mostly before the lane was cut)
// and 4.6% (26 people) clicked through to the App Store. Both rates fell after
// the rebuild — signup 0.307 to 0.216, app click 0.050 to 0.027 — but at seven
// days and n=111 neither move is significant. The honest read is that the
// app-led pivot has not started working YET, not that it was wrong.
//
// So this is a hedge, not a reversal: the site's single largest signup source
// is not the place to run an unproven single-lane bet. OAuth leads because it
// is the measured converter here, email is the lane for anyone who will not
// hand over a Google or Apple account, and the app keeps a lane of its own.
// Re-read the app lane on its own numbers before demoting anything again; a
// lane that is not offered cannot be measured.
//
// PRESENTATION (2026-08-08, Claude Design "Limit Wall" variant 1b): the old
// ~1,500px three-divider stack is now one frame — the visitor's photo as a
// full-bleed anchor with a blurred decoy result tease, pill OAuth buttons as
// the lead ask, the app as a compact one-row card, and email collapsed into a
// <details> disclosure. Visual order changed with it: account, app, email.
// The photo|content split keys off CONTAINER width (@xl), not viewport — the
// wall renders inside ~950px (home), ~670px (diagnose), and ~510px (weed)
// parents, and those parents drop their own padding while this wall shows so
// the photo can bleed to the card edge.
//
// PHONE ORDER (2026-09-12): on a coarse-pointer device where the app link is
// usable, the app row renders ABOVE the account lane. 28 days of iPhone data
// said the secondary slot was not being found: 299 iPhone visitors reached the
// wall and 5 clicked through to the App Store. The lead row is also the only
// place on this wall where "another free scan, now" is literally true — the
// native client keeps its own device-keyed anonymous counter, so an install
// hands back an allowance rather than queueing for the spent web one. Desktop
// is unchanged (an App Store link there is a QR handoff, not an action) and
// Android is untouched. `app_lane_position` on analysis_resume_wall_shown
// splits the two layouts for the read.
//
// The email lane raises the anonymous ceiling from 1 to 3 via
// EMAIL_UNLOCK_COOKIE (lib/email-unlock-cookie.ts), set server-side by
// /api/email/subscribe on both a fresh signup and an already-subscribed 409.
// It is hidden at tier 'email' — that visitor already gave an address and
// spent the allowance it bought, so re-asking would be a dead end.

interface AnonQuotaNoticeProps {
  mode: AnalysisMode
  /** Why the device's anon quota is spent — see lib/anon-quota-flag. */
  reason: AnonQuotaReason | null
  /** Extra monthly scans a free account unlocks vs anonymous. */
  extraScans: number
  /** Where the login page should return the user after signup. */
  redirectPath: string
}

// Impressions are deduped at module scope, not per instance. This notice is
// mounted inside an AnimatePresence branch that unmounts on every mode toggle
// and upload-state change, so a component-lifetime ref re-fired
// `anon_quota_notice_shown` on each remount and inflated the impression count
// against a flat click count. Module state survives remounts for the life of
// the page (same trick as lib/pending-scan-file) and resets on a hard reload,
// which is the correct grain for a "did this visit see it" impression.
const shownImpressions = new Set<string>()

/** Test-only: clear the module-level impression ledger between cases. */
export function __resetAnonQuotaNoticeImpressions(): void {
  shownImpressions.clear()
}

/**
 * Pre-upload heads-up for users whose device is genuinely walled — the last
 * analyze attempt came back `signupRequired` (`reason === 'blocked'`). Shown
 * ABOVE the upload card so the wall is never a surprise sprung after the photo
 * + ZIP effort. Informational, not blocking — the user can still try
 * (allowlisted IPs and month rollovers succeed, and the attempt itself
 * refreshes the stale flag).
 *
 * Renders NOTHING for `reason === 'consumed'`. With an anon limit of 1, every
 * successful scan sets the exhaustion flag, so an audience keyed on "flag set"
 * showed this to everyone who had just finished a scan rather than to the
 * walled minority it is written for.
 */
export function AnonQuotaNotice({ mode, reason, extraScans, redirectPath }: AnonQuotaNoticeProps) {
  const { singular: allowanceNoun, plural: scanNounPlural } = allowanceLabels(mode)
  const blocked = reason === 'blocked'

  useEffect(() => {
    if (!blocked) return
    const key = `${mode}:${currentQuotaMonth()}`
    if (shownImpressions.has(key)) return
    shownImpressions.add(key)
    trackEvent("anon_quota_notice_shown", { mode })
  }, [blocked, mode])

  if (!blocked) return null

  return (
    // Live region: the notice mounts post-hydration (after the localStorage
    // read), so without this assistive tech never hears the quota warning.
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="mx-auto mb-4 flex max-w-md items-start gap-2.5 rounded-xl border border-amber-200/80 bg-amber-50/70 px-3.5 py-2.5 text-left"
    >
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
      <p className="text-[13px] leading-snug text-amber-900">
        This month&apos;s free no-account {allowanceNoun} is already used.{" "}
        <Link
          href={`/auth/login?redirect=${encodeURIComponent(redirectPath)}`}
          onClick={() => trackEvent("anon_quota_notice_clicked", { mode })}
          className="font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
        >
          Create a free account
        </Link>{" "}
        for up to 3 photos per scan, a free refinement when a result is close, and{" "}
        {extraScans} more {scanNounPlural} this month.{allowanceNote(mode)}
      </p>
    </div>
  )
}

/**
 * The photo header carries a SHAPE-ONLY result tease — two blurred bars, no
 * text. An earlier version put a plausible decoy species and match percentage
 * here ("Tall Fescue · 92% match"). That is not safe on this surface: blur is
 * not redaction, a determined reader resolves short bold text through it, and
 * anyone who does walks away believing a grass ID we never ran. Bars carry the
 * same "your result is behind this" tension and assert nothing.
 *
 * Rules if this block is ever revisited: no species names, no percentages, no
 * model output, and no pulse animation (pulse reads as "still analyzing" when
 * nothing is analyzing).
 */

interface AnonLimitWallProps {
  mode: AnalysisMode
  imageSrc: string | null
  oauthRedirectTo: string
  /** Extra monthly scans a free account unlocks vs anonymous. */
  extraScans: number
  appLinkPlacement: string
  /**
   * The ZIP the visitor typed on the step before this wall, used to name their
   * climate in the ask. Optional: the diagnose and weed flows can reach the wall
   * with the field still empty.
   */
  zipCode?: string
  /**
   * Which rung of the anonymous ladder this visitor is on. Drives both the copy
   * (what they actually exhausted, what an account adds) and whether the email
   * lane renders at all — at 'email' the ceiling it buys is already spent.
   * Defaults to 'anon' so older callers keep today's behavior.
   */
  tier?: 'anon' | 'email'
  /**
   * Optional in-place resume after the email lane unlocks the higher ceiling.
   * When a caller passes it, the wall hands control back instead of navigating,
   * which keeps the photo in React state. Every caller today omits it, so the
   * fallback below re-enters through `oauthRedirectTo` — the same first-party
   * URL the OAuth lane uses, which carries the photo and ZIP as params and
   * rehydrates for anonymous visitors too.
   */
  onEmailUnlocked?: () => void
}

/** Source string for the email lane. Doubles as the subscribe API `context`,
 *  the PostHog signup `source`, and the OAuth attribution stash, so the
 *  impression → capture → signup funnel joins on one value. */
const EMAIL_LANE_SOURCE = 'anon_limit_wall'

export function AnonLimitWall({
  mode,
  imageSrc,
  oauthRedirectTo,
  extraScans,
  appLinkPlacement,
  zipCode,
  tier = 'anon',
  onEmailUnlocked,
}: AnonLimitWallProps) {
  const { singular: resultNoun } = resultLabels(mode)
  const { singular: allowanceNoun, plural: scanNounPlural } = allowanceLabels(mode)
  const shownFiredRef = useRef(false)
  // Boolean experiment: false or unresolved preserves the collapsed control.
  // The treatment opens the existing lane; it does not change its offer, API,
  // allowance, or position in the wall.
  const emailVisibilityOpen = useFeatureFlagEnabled("quota_email_visibility_v1") === true
  const emailVisibilityVariant = emailVisibilityOpen ? "open" : "control"
  // Android hides the app block: the App Store link dead-ends there. iOS and
  // desktop both keep it (desktop converts through the QR handoff below).
  const appInstallAvailable = useAppInstallAvailable()
  // Phones put the app FIRST. A walled iPhone visitor genuinely gets a fresh
  // free scan by installing — the native client keeps its own device-keyed
  // anonymous counter (obsidian/decisions/anonymous-app-scans-match-web-allowance.md)
  // — so on the one device that can act on an App Store link immediately, the
  // install is a way forward rather than a footnote under the OAuth buttons.
  // Desktop keeps the account-led order, where the app lane is a QR handoff
  // and the install cannot happen on the device in front of the visitor.
  //
  // pointer:coarse, the same signal the QR gate below uses, rather than a
  // second user-agent sniff or a width query.
  const coarsePointer = useCoarsePointer()
  const appLeadsLane = appInstallAvailable && coarsePointer

  const emailFieldId = `anon-limit-wall-email-${useId()}`
  const [email, setEmail] = useState('')
  const [emailStatus, setEmailStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailSuggestion, setEmailSuggestion] = useState<EmailSuggestion | null>(null)
  const emailFocusFiredRef = useRef(false)

  // What an email actually buys THIS visitor, read from the ladder rather than
  // hardcoded, so a limit change can't leave the wall promising a stale number.
  const emailExtraScans = Math.max(
    0,
    EMAIL_TIER_ANALYSIS_LIMITS[mode] - ANONYMOUS_WEB_ANALYSIS_LIMITS[mode],
  )
  const showEmailLane = tier === 'anon' && emailExtraScans > 0

  // Name what the visitor ACTUALLY exhausted. "free no-account" is wrong for
  // someone on the email tier: they gave us an address and spent the larger
  // allowance it bought.
  //
  // Stated about the ALLOWANCE, not about the person. The earlier phrasing
  // opened "You've used this month's free...", which lands as an accusation
  // whenever the visitor does not remember the scan — a different month, a
  // different household member, or a shared IP. They cannot check it from
  // here, so second person invites an argument we always appear to lose. The
  // subject is the quota; the verb carries the tier's own number.
  const spentSentence =
    tier === 'email'
      ? `This month's free ${scanNounPlural} are already used.`
      : `This month's free no-account ${allowanceNoun} is already used.`

  // EMAIL_TIER_ANALYSIS_LIMITS currently equals REGISTERED_FREE_ANALYSIS_LIMITS
  // (3 per mode), so an account buys an email-tier visitor exactly zero extra
  // scans this month. Promising "N more" there would be a lie the very next tap
  // disproves, so that rung is sold on what an account actually adds instead:
  // persistence. Revisit this branch if the two limits ever diverge.
  const accountAddsScans = tier === 'anon' && extraScans > 0

  // Route half of the gate `surface`, read from the OAuth destination so it
  // names the flow the visitor was walled in rather than wherever this
  // component happens to be mounted.
  let entryPathForSurface: string | null = null
  try {
    entryPathForSurface = new URL(oauthRedirectTo, 'https://whatgrassisthis.invalid').pathname
  } catch { /* normalized by AuthContext */ }

  const resumeContext = () => {
    let analysisResumeId: string | undefined
    let entryPath: string | undefined
    let imageCount: number | undefined
    try {
      const destination = new URL(oauthRedirectTo, 'https://whatgrassisthis.invalid')
      analysisResumeId = destination.searchParams.get('analysisResumeId') || undefined
      entryPath = destination.pathname
      imageCount = destination.searchParams.has('imageUrl')
        ? 1 + destination.searchParams.getAll('supportingImageUrl').length
        : undefined
    } catch { /* normalized by AuthContext */ }

    return {
      analysis_resume_id: analysisResumeId,
      mode,
      entry_path: entryPath,
      image_count: imageCount,
      // Which ask the visitor actually saw. Without these two the funnel can't
      // tell an Android visitor (no app block) from an iPhone one, or a
      // first-wall visitor from someone who already spent the email tier.
      tier,
      app_install_available: appInstallAvailable,
      email_visibility_variant: showEmailLane ? emailVisibilityVariant : 'not_shown',
    }
  }

  useEffect(() => {
    if (shownFiredRef.current) return
    shownFiredRef.current = true
    // Held until the tab is actually foregrounded: a background-tab open or a
    // SERP prerender runs this effect but receives no $pageview, so an
    // ungated impression inflates the denominator of every rate read off this
    // wall.
    return runWhenPageVisible(() => {
      // Both gates read DIRECTLY rather than through their hooks. On the
      // hydration render each still reports its server snapshot, so sampling
      // the hook values here would label an iPhone's app-led wall 'secondary'
      // and split the layouts wrong.
      const appLanePosition: 'lead' | 'secondary' =
        appInstallAvailableNow() && coarsePointerNow() ? 'lead' : 'secondary'
      analytics.analysisResumeWallShown(
        {
          mode,
          // The wall only renders after the analyze route walled the attempt, so
          // the rung the visitor is on IS the reason they are looking at it.
          reason: tier === 'email'
            ? 'email_tier_quota_exhausted'
            : 'anon_quota_exhausted',
          surface: `${surfaceFromPathname(entryPathForSurface)}:analysis_resume_wall`,
        },
        { ...resumeContext(), app_lane_position: appLanePosition },
      )
      // Impression for the email lane specifically, so this surface appears in
      // the same email_capture_surface_shown -> newsletter_signup funnel every
      // other capture surface joins. Emitted through trackEvent rather than
      // analytics.surfaceShown so the wall keeps working under the analytics
      // mocks used by page-level tests; `surface_family` is spelled out because
      // emailSurfaceFamily() passes this source through its default branch.
      if (showEmailLane) {
        trackEvent('email_capture_surface_shown', {
          source: EMAIL_LANE_SOURCE,
          surface_family: EMAIL_LANE_SOURCE,
          mode,
          app_install_available: appInstallAvailable,
          email_visibility_variant: emailVisibilityVariant,
        })
      }
    })
  // The wall is mounted per attempt; do not re-fire if the redirect URL is
  // enriched after the quota response updates state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const beforeOAuth = (provider: 'google' | 'apple') => {
    const context = resumeContext()
    stashEmailCaptureAttribution(EMAIL_LANE_SOURCE)

    const properties = {
      ...context,
      provider,
    }
    trackEvent('analysis_resume_auth_started', properties)
    // The scan funnel id rides on oauth_initiated and the placement stash, so a
    // walled scan joins to the account it produced.
    return { ...properties, funnel_id: ensureAnalysisFunnelId() }
  }

  // Scan join keys for the email lane's capture events. The wall has no
  // analysis_id (the scan was walled before it ran), so analysis_resume_id plus
  // the session funnel id are the join.
  const captureJoinKeys = () => ({
    funnel_id: ensureAnalysisFunnelId(),
    analysis_resume_id: resumeContext().analysis_resume_id,
  })

  async function handleEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (emailStatus === 'submitting') return

    const value = email.trim()
    if (!value || !value.includes('@')) {
      setEmailError('Please enter a valid email address')
      setEmailStatus('error')
      return
    }

    setEmailStatus('submitting')
    setEmailError(null)
    // Same event as the OAuth lane so both rails of the wall share one funnel;
    // `provider: 'email'` is what separates them downstream.
    trackEvent('analysis_resume_auth_started', { ...resumeContext(), provider: 'email' })

    try {
      const response = await fetch('/api/email/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, context: EMAIL_LANE_SOURCE }),
      })
      const data = await response.json().catch(() => null)

      // A returning subscriber gets the unlock cookie too (the route sets it on
      // the already_subscribed 409), so treat it as success — telling someone
      // their address "failed" while the server just unlocked them is the worst
      // possible end to this lane.
      if (response.ok || data?.error === 'already_subscribed') {
        trackEvent('analysis_resume_email_captured', {
          ...resumeContext(),
          provider: 'email',
          status_code: response.status,
          returning_subscriber: data?.error === 'already_subscribed',
        })
        analytics.newsletterSignup(value, EMAIL_LANE_SOURCE, null, null, captureJoinKeys())
        // 5-minute attribution window: if they go on to create an account from
        // here, the signup carries this wall as its capture_source.
        stashEmailCaptureAttribution(EMAIL_LANE_SOURCE)
        setEmailStatus('success')
        return
      }

      setEmailError(
        response.status === 429
          ? 'Too many tries. Wait a few minutes, or continue with Google or Apple above.'
          : data?.error || 'Something went wrong. Please try again.',
      )
      trackEvent('analysis_resume_email_failed', {
        ...resumeContext(),
        provider: 'email',
        status_code: response.status,
        failure_reason: response.status === 429 ? 'rate_limited' : 'subscribe_rejected',
      })
      setEmailStatus('error')
    } catch {
      trackEvent('analysis_resume_email_failed', {
        ...resumeContext(),
        provider: 'email',
        status_code: null,
        failure_reason: 'network_error',
      })
      setEmailError('Network error. Check your connection and try again.')
      setEmailStatus('error')
    }
  }

  // The unlock is a server cookie, so the scan only runs once the page reads it
  // again. A caller that owns the analysis state can resume in place; otherwise
  // re-enter through the resume URL, which carries the photo and ZIP.
  function resumeAfterEmailUnlock() {
    trackEvent('analysis_limit_restored', { ...resumeContext(), unlock: 'email' })
    if (onEmailUnlocked) {
      onEmailUnlocked()
      return
    }
    if (typeof window !== 'undefined') window.location.assign(oauthRedirectTo)
  }

  return (
    <div className="@container text-left">
      {/* `auto` sizes the photo column to the square itself. A fractional
          column left the square floating in dead space once it stopped
          stretching to full height. */}
      <div className={imageSrc ? "grid @xl:grid-cols-[auto_minmax(0,1fr)]" : undefined}>
        {imageSrc && (
          // Sizing: a banner strip on narrow containers, a CONTAINED SQUARE on
          // wide ones. The pane used to stretch to the ask column's height
          // (min-h-[460px]), which made a tall, narrow frame that center-cropped
          // most of a normal landscape lawn photo away. object-cover never
          // distorts — it preserves the aspect ratio and crops — so the fix is
          // the frame's shape, not the fit mode. A square crops least from both
          // portrait and landscape sources, and self-center keeps it beside the
          // ask instead of stretching to meet it.
          //
          // self-CENTER, not self-start. The ask column runs roughly twice the
          // square's height, so pinning the photo to the top left one tall hole
          // under it and the frame read as broken rather than as a photo beside
          // an ask. Centering splits that slack above and below, which reads as
          // deliberate. Do not "fix" the remaining space by stretching the
          // photo — that is the tall-narrow crop this frame was shaped to end.
          <div className="relative h-40 overflow-hidden @xl:m-6 @xl:aspect-square @xl:h-auto @xl:w-[260px] @xl:self-center @xl:rounded-2xl">
            <img
              src={imageSrc}
              alt="Your lawn photo"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div
              className="absolute inset-0 bg-linear-to-b from-transparent via-transparent to-gray-900/50"
              aria-hidden="true"
            />
            <div className="absolute left-3 top-3 hidden items-center gap-1.5 rounded-full bg-white/85 px-3 py-1.5 text-xs font-semibold text-green-800 backdrop-blur-md @xl:flex">
              <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
              Your photo · saved
            </div>
            {/* Stacked, not side by side: the square frame is ~260px wide and
                a row layout clipped the lock pill. Stacking fits both the
                square and the wide mobile banner. */}
            <div className="absolute inset-x-3 bottom-3 flex flex-col items-start gap-2.5 rounded-2xl border border-white/60 bg-white/75 px-4 py-3 backdrop-blur-lg">
              <div className="pointer-events-none select-none space-y-2 blur-[3px]" aria-hidden="true">
                <div className="h-4 w-32 rounded-full bg-gray-900/30 @xl:h-5 @xl:w-40" />
                <div className="h-2.5 w-24 rounded-full bg-gray-900/20 @xl:h-3 @xl:w-28" />
              </div>
              <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-gray-900 px-3.5 py-2 text-xs font-semibold text-white">
                <Lock className="h-3 w-3" aria-hidden="true" />
                One tap away
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4 p-5 @xl:justify-center @xl:gap-5 @xl:p-8">
          <div>
            <h2 className="font-poppins text-[22px] font-extrabold leading-tight tracking-[-0.025em] text-gray-900 @xl:text-[28px]">
              Your {resultNoun} is one step away
            </h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-gray-600 @xl:text-[15px]">
              {spentSentence} Sign in and this photo runs immediately, no
              re-upload. You get {payoffLabel(mode, zipCode)}.
              {/* Sells the capability first, then the quota. An account is
                  not only "three more of the same": it reads up to 3 photos
                  at once and refines a close call for free. */}
              {accountAddsScans
                ? ` A free account adds up to 3 photos per scan, a free refinement when a result is close, and ${extraScans} more ${scanNounPlural} this month.`
                : " A free account adds up to 3 photos per scan, a free refinement when a result is close, and results saved on every device."}
              {allowanceNote(mode)}
            </p>
          </div>

          {/* On a phone the app leads. The promise is ALLOWANCE, never speed:
              the headline above says this photo runs immediately on sign-in,
              which an install cannot match, so the app sells the one thing it
              genuinely has here — a counter the web visitor has not spent —
              plus the timing the web cannot deliver. Do not reintroduce a
              "scan in seconds" line; it puts the two asks in contradiction. */}
          {appLeadsLane && (
            <div className="rounded-2xl border border-gray-200 bg-white/90 p-3.5">
              <div className="flex items-center gap-3">
                <img
                  src="/apple-touch-icon.png"
                  alt=""
                  width={44}
                  height={44}
                  loading="lazy"
                  className="h-11 w-11 shrink-0 rounded-[10px]"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold leading-snug text-gray-900">
                    Get another free scan now in the app
                  </div>
                  <div className="mt-0.5 text-xs leading-snug text-gray-500">
                    Fresh scan allowance, plus reminders timed to your lawn.
                  </div>
                </div>
              </div>
              {/* Full-width button rather than the desktop card's trailing one:
                  the lead headline needs the whole row, and a 44px-tall target
                  spanning the card is the easiest thing to hit with a thumb. */}
              <AppStoreLinkButton
                placement={appLinkPlacement}
                label="App Store"
                className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-white transition-colors hover:bg-black"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-4 w-4 shrink-0">
                  <path d={APPLE_GLYPH_PATH} />
                </svg>
                <span className="whitespace-nowrap text-sm font-bold">App Store</span>
              </AppStoreLinkButton>
            </div>
          )}

          {/* The account lane. It leads on desktop, where an App Store link is
              a QR handoff rather than something the visitor can act on now;
              under the app row on phones. Either way it keeps its own copy and
              the reassurance line directly beneath it. */}
          <div>
            <SocialLoginButtons
              placement="anon_limit_wall"
              redirectTo={oauthRedirectTo}
              onBeforeRedirect={beforeOAuth}
              pill
              appleSolid
            />
            <p className="mt-2.5 text-center text-xs text-gray-500">
              Free · No credit card · No spam
            </p>
          </div>

          {/* The app in its SECONDARY position (desktop and anything else with
              a fine pointer; phones get the lead row above instead) — one
              compact row instead of the old
              rows-screenshot-badge stack, so it shares a viewport with the
              account ask instead of living below the fold of the wall.

              It does NOT sell scan speed. The headline promises that THIS photo
              runs immediately with no re-upload, and the app is the one lane
              that cannot deliver that: it needs a download, an install, and a
              fresh scan. Selling "point, scan, done in seconds" here put the
              two asks in direct contradiction and made the app the slower
              version of the thing above it. So the lane sells what the web
              genuinely cannot do instead — season timing the visitor gets no
              other way. */}
          {appInstallAvailable && !appLeadsLane && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white/90 p-3 @xl:px-4 @xl:py-3.5">
                <img
                  src="/apple-touch-icon.png"
                  alt=""
                  width={44}
                  height={44}
                  loading="lazy"
                  className="h-10 w-10 shrink-0 rounded-[10px] @xl:h-11 @xl:w-11"
                />
                <div className="min-w-0 flex-1">
                  {/* Kept short on purpose. The text column is only ~150px wide
                      between the icon and the App Store button at 390px, so a
                      longer sell wraps to three or four lines and blows out the
                      row's height. */}
                  <div className="text-[13px] font-bold leading-snug text-gray-900 @xl:text-sm">
                    Or get season reminders
                  </div>
                  <div className="text-[11px] leading-snug text-gray-500 @xl:text-xs">
                    Free <span className="hidden @xl:inline">iPhone </span>app.
                    Reminders when your window opens.
                  </div>
                </div>
                <AppStoreLinkButton
                  placement={appLinkPlacement}
                  label="App Store"
                  className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl bg-gray-900 px-3.5 py-2 text-white transition-colors hover:bg-black"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-4 w-4 shrink-0">
                    <path d={APPLE_GLYPH_PATH} />
                  </svg>
                  <span className="whitespace-nowrap text-[13px] font-bold">App Store</span>
                </AppStoreLinkButton>
              </div>
              {/* Desktop QR handoff — same pattern and asset as AppDownloadCTA.
                  Gated on pointer:fine (pure CSS, SSR-safe) so it shows only on
                  mouse/trackpad devices, never on a phone or tablet where the
                  badge already works and a QR is pointless. */}
              <div className="hidden items-center gap-2.5 [@media(pointer:fine)]:flex">
                <AppStoreQrCode
                  placement={appLinkPlacement}
                  size={52}
                  className="h-[52px] w-[52px] rounded-lg border border-green-200 p-1"
                />
                <span className="max-w-[180px] text-left text-xs leading-snug text-gray-500">
                  On your computer? Scan with your iPhone camera.
                </span>
              </div>
            </div>
          )}

          {/* Lane 3, the email — collapsed to a disclosure. Still the route
              forward for anyone who will not hand over a Google or Apple
              account; the impression event keeps firing on mount because the
              lane (its summary line) is on screen even while collapsed. */}
          {showEmailLane && (
            emailStatus === 'success' ? (
              <div
                role="status"
                aria-live="polite"
                className="rounded-2xl border border-green-200 bg-green-50/70 px-4 py-3 text-left"
              >
                <p className="flex items-start gap-2 text-sm font-semibold text-green-900">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" aria-hidden="true" />
                  You&apos;re unlocked. {emailExtraScans} more {scanNounPlural} this month.
                </p>
                <Button
                  onClick={resumeAfterEmailUnlock}
                  className="mt-3 w-full min-h-[44px] rounded-full bg-green-700 px-4 py-3 text-sm font-semibold text-white hover:bg-green-800"
                >
                  Run my {resultNoun} now
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            ) : (
              <details
                open={emailVisibilityOpen ? true : undefined}
                className="border-t border-gray-100 pt-1"
              >
                <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-center text-center text-[13px] font-medium text-green-700 transition-colors hover:text-green-800 [&::-webkit-details-marker]:hidden">
                  Prefer email? Unlock {emailExtraScans} more {scanNounPlural} without an account
                </summary>
                <form onSubmit={handleEmailSubmit} className="mt-2 space-y-2.5 text-left">
                  <label htmlFor={emailFieldId} className="sr-only">
                    Email address
                  </label>
                  <div className="flex flex-col gap-2.5">
                    <input
                      id={emailFieldId}
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        const value = e.target.value
                        setEmail(value)
                        setEmailSuggestion(
                          value.includes('@') && value.includes('.') ? detectEmailTypo(value) : null,
                        )
                        if (emailStatus === 'error') {
                          setEmailStatus('idle')
                          setEmailError(null)
                        }
                      }}
                      onFocus={() => {
                        if (emailFocusFiredRef.current) return
                        emailFocusFiredRef.current = true
                        const startProps = {
                          ...resumeContext(),
                          ...captureJoinKeys(),
                          source: EMAIL_LANE_SOURCE,
                          surface_family: EMAIL_LANE_SOURCE,
                        }
                        // email_capture_started is the site-wide start event;
                        // the legacy name keeps firing for continuity.
                        trackEvent('email_capture_started', startProps)
                        trackEvent('result_email_capture_focused', startProps)
                      }}
                      disabled={emailStatus === 'submitting'}
                      aria-invalid={emailStatus === 'error'}
                      className="h-12 w-full rounded-full border border-gray-300 bg-white px-5 text-base shadow-sm transition placeholder:text-gray-400 focus:border-green-600 focus:outline-hidden focus:ring-2 focus:ring-green-500/50 disabled:opacity-60 aria-[invalid=true]:border-red-400"
                    />
                    <Button
                      type="submit"
                      disabled={emailStatus === 'submitting'}
                      className="min-h-[48px] w-full rounded-full bg-green-700 px-5 py-3 text-sm font-semibold text-white hover:bg-green-800 disabled:opacity-60"
                    >
                      {emailStatus === 'submitting' ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        `Unlock ${emailExtraScans} more ${scanNounPlural}`
                      )}
                    </Button>
                  </div>

                  {emailSuggestion && (
                    <button
                      type="button"
                      onClick={() => {
                        setEmail(emailSuggestion.suggested)
                        setEmailSuggestion(null)
                      }}
                      className="text-sm text-green-700 hover:underline"
                    >
                      Did you mean <span className="font-semibold">{emailSuggestion.suggested}</span>?
                    </button>
                  )}

                  {emailStatus === 'error' && emailError && (
                    <p role="alert" className="flex items-center gap-1.5 text-sm text-red-700">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      {emailError}
                    </p>
                  )}
                </form>
              </details>
            )
          )}
        </div>
      </div>
    </div>
  )
}

interface PremiumLimitWallProps {
  mode: AnalysisMode
  imageSrc: string | null
  pricingHref?: string
  appLinkPlacement: string
}

export function PremiumLimitWall({
  mode,
  imageSrc,
  pricingHref = "/pricing",
  appLinkPlacement,
}: PremiumLimitWallProps) {
  const isDiagnose = mode === "diagnose"
  const isWeed = mode === "weed"
  const scanNounPlural = isDiagnose ? "diagnoses" : isWeed ? "weed IDs" : "identifications"
  const shownFiredRef = useRef(false)

  // Impression parity with AnonLimitWall. Without this the registered-free
  // wall was the one limit surface with no `shown` event, so its conversion
  // could only ever be measured against `analysis_upgrade_required` (the
  // server denial) rather than against what the user actually saw.
  useEffect(() => {
    if (shownFiredRef.current) return
    shownFiredRef.current = true
    trackEvent('premium_limit_wall_shown', { mode })
  }, [mode])

  return (
    <div className="space-y-5">
      {imageSrc && (
        <img
          src={imageSrc}
          alt="Your lawn photo"
          className="w-32 h-32 rounded-2xl object-cover border-2 border-green-200 mx-auto"
        />
      )}

      <div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
          Your photo is ready. Premium runs it now
        </h2>
        <p className="text-sm text-gray-600">
          This month&apos;s free analyses are already used. Premium adds unlimited{" "}
          {scanNounPlural}, unlimited care plans, and smart reminders.
        </p>
      </div>

      {/* Capability before quota. The first two lines are things a free
          account cannot do at all; the third is the count. */}
      <div className="flex flex-col items-center gap-2 text-sm text-gray-600">
        <span className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          Up to 5 photos per scan for a harder call
        </span>
        <span className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          Unlimited refinements when a result is close
        </span>
        <span className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          Unlimited lawn {scanNounPlural} and care plans
        </span>
      </div>

      <Button asChild className="w-full bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 px-6 rounded-xl font-semibold text-base min-h-[48px]">
        <Link href={pricingHref}>
          Unlock unlimited {scanNounPlural}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </Button>

      <AppDownloadTextLink
        placement={appLinkPlacement}
        label={`More free ${isDiagnose ? "diagnoses" : isWeed ? "weed IDs" : "scans"} on the free iPhone app`}
      />

      <p className="text-xs text-gray-400">Resets next month</p>
    </div>
  )
}

interface PhotoCapGateProps {
  mode: AnalysisMode
  /** 'photo_cap' = the server rejected the set that was sent. 'refinement' =
   *  the visitor tapped a refinement CTA on a result or a locked slot in the
   *  refinement panel. */
  intent: 'photo_cap' | 'refinement'
  /** Which offer the visitor tapped to get here, so refinement gate views stay
   *  separable by lane in the funnel. */
  trigger?: RefinementTrigger | null
  requiredTier: 'registered_free' | 'premium'
  /** Photos this visitor may send today, primary included. */
  cap: number
  /** Photos the rejected request carried. Ignored for the refinement intent. */
  requested?: number
  /** Which 403 the server sent, for the 'photo_cap' intent. 'additive_cap'
   *  means the set was inside the cap and the free refinement behind it is
   *  spent, so the photo-count sentence would be false. */
  denialReason?: PhotoLimitReason | null
  /** The model's own ask for the next view, when we have one. */
  guidance?: string | null
  imageSrc?: string | null
  oauthRedirectTo: string
  pricingHref?: string
  onDismiss?: () => void
}

/**
 * The refinement gate's headline and body describe the state the result is
 * actually in. `trigger` is the reason the visitor arrived, so "close call"
 * copy is reserved for the reasons where the model really was undecided; a
 * settled result reached through the default retry pair gets the additive
 * framing instead.
 */
function refinementGateCopy(
  resultNoun: string,
  trigger: RefinementTrigger | null,
): { headline: string; body: string } {
  if (trigger === 'retry_additive') {
    return {
      headline: "Read two photos of this lawn together",
      body: "A free account reads up to 3 photos in one pass, so the second view is weighed against the first instead of replacing it. Your photo and ZIP stay right here.",
    }
  }
  if (trigger === 'conflicting_rescan') {
    return {
      headline: "Settle which answer holds",
      body: "A free account reads up to 3 photos at once, so one more view breaks the tie instead of overwriting the first answer. Your photo and ZIP stay right here.",
    }
  }
  return {
    headline: `Sharpen this ${resultNoun} with one more photo`,
    body: "A free account reads up to 3 photos at once and refines a close call for free. Your photo and ZIP stay right here.",
  }
}

/**
 * The photo-count gate. Deliberately NOT a quota wall: nothing here is
 * exhausted, so the copy names what the next tier reads rather than what this
 * visitor spent (same honest-decline framing as AnonLimitWall's spentSentence).
 *
 * It renders for two moments that share one ask — a rejected multi-photo
 * request, and an anonymous tap on a refinement CTA.
 */
export function PhotoCapGate({
  mode,
  intent,
  trigger = null,
  requiredTier,
  cap,
  requested,
  denialReason = null,
  guidance,
  imageSrc,
  oauthRedirectTo,
  pricingHref = "/pricing",
  onDismiss,
}: PhotoCapGateProps) {
  const { singular: resultNoun } = resultLabels(mode)
  const shownFiredRef = useRef(false)

  useEffect(() => {
    if (shownFiredRef.current) return
    shownFiredRef.current = true
    trackEvent('photo_cap_gate_shown', {
      mode,
      intent,
      trigger,
      required_tier: requiredTier,
      requested: requested ?? null,
      cap,
      reason: denialReason,
      platform: 'web',
    })
  }, [cap, denialReason, intent, mode, requested, requiredTier, trigger])

  const isSignIn = requiredTier === 'registered_free'
  const appLinkPlacement = photoGatePlacement(mode, intent)
  const refinementCopy = refinementGateCopy(resultNoun, trigger)
  // An 'additive_cap' denial was inside the photo cap: the free refinement for
  // this result is what ran out, so the photo-count sentence would be false.
  const isAdditiveCap = intent === 'photo_cap' && denialReason === 'additive_cap'
  const headline = isAdditiveCap
    ? "This result used its free refinement"
    : intent === 'refinement'
      ? refinementCopy.headline
      : "This needs more than one photo"
  const body = isAdditiveCap
    ? "Your free targeted refinement for this result is used. Premium refines without limits."
    : intent === 'refinement'
    ? refinementCopy.body
    : isSignIn
      ? `You sent ${requested ?? cap + 1} views. A free account reads up to 3 at once.`
      : `You sent ${requested ?? cap + 1} views. Premium reads up to 5 at once.`

  return (
    <div className="mx-auto max-w-md space-y-5 text-center">
      {imageSrc && (
        <img
          src={imageSrc}
          alt="Your lawn photo"
          className="ph-no-capture mx-auto h-32 w-32 rounded-2xl border-2 border-green-200 object-cover"
          data-ph-no-capture="true"
        />
      )}

      <div>
        <h2 className="font-poppins text-[22px] font-extrabold leading-tight tracking-[-0.025em] text-gray-900">
          {headline}
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-gray-600">
          {/* An additive-cap panel refuses the very photo the guidance asks for,
              so the ask is dropped here and only the upgrade story remains. */}
          {guidance && !isAdditiveCap ? `${guidance} ` : ""}{body}
        </p>
      </div>

      {isSignIn ? (
        <div>
          <SocialLoginButtons
            placement="photo_cap_gate"
            redirectTo={oauthRedirectTo}
            onBeforeRedirect={() => {
              stashEmailCaptureAttribution('photo_cap_gate')
              return { gate_mode: mode, gate_intent: intent, funnel_id: ensureAnalysisFunnelId() }
            }}
            pill
            appleSolid
          />
          <p className="mt-2.5 text-center text-xs text-gray-500">
            Free · No credit card · No spam
          </p>
        </div>
      ) : (
        <Button asChild className="min-h-[48px] w-full rounded-xl bg-linear-to-r from-green-600 to-emerald-600 px-6 py-3 text-base font-semibold text-white hover:from-green-700 hover:to-emerald-700">
          <Link href={pricingHref} onClick={() => trackEvent('photo_cap_gate_clicked', { mode, required_tier: requiredTier })}>
            {/* The photo ceiling was never the wall on an additive-cap denial,
                so selling five photos sells a benefit this caller never hit. */}
            {isAdditiveCap ? "Unlock unlimited refinements" : "Read up to 5 photos with Premium"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      )}

      <AppDownloadTextLink
        placement={appLinkPlacement}
        label="Get season reminders in the free iPhone app"
      />

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="min-h-[44px] text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
        >
          Keep my current result
        </button>
      )}
    </div>
  )
}
